import type { Dispatch, SetStateAction } from 'react'
import {
    DEFAULT_AUTOMATION_SAFETY,
    type AutomationSafety,
} from '@/lib/automationSafetyDefaults'

type Props = {
    advancedOptionsState: Record<string, unknown>
    setAdvancedOptionsState: Dispatch<SetStateAction<Record<string, unknown>>>
    showToggleSection?: boolean
    showDoseSection?: boolean
    showHysteresisSection?: boolean
}

function clampInt(v: number, min: number, max: number, fallback: number) {
    if (!Number.isFinite(v)) return fallback
    return Math.min(max, Math.max(min, Math.round(v)))
}

function clampFloat(v: number, min: number, max: number, fallback: number) {
    if (!Number.isFinite(v)) return fallback
    return Math.min(max, Math.max(min, v))
}

const AutomationSafetySettings = ({
    advancedOptionsState,
    setAdvancedOptionsState,
    showToggleSection = true,
    showDoseSection = true,
    showHysteresisSection = true,
}: Props) => {
    const as = (advancedOptionsState.automation_safety ?? {}) as Partial<AutomationSafety>
    const toggle = { ...DEFAULT_AUTOMATION_SAFETY.toggle, ...as.toggle }
    const dose = { ...DEFAULT_AUTOMATION_SAFETY.dose, ...as.dose }
    const hysteresis = { ...DEFAULT_AUTOMATION_SAFETY.hysteresis, ...as.hysteresis }

    const patch = (partial: Partial<AutomationSafety>) => {
        setAdvancedOptionsState((prev) => {
            const p = prev as Record<string, unknown>
            const cur = (p.automation_safety ?? {}) as Partial<AutomationSafety>
            return {
                ...p,
                automation_safety: {
                    toggle: { ...DEFAULT_AUTOMATION_SAFETY.toggle, ...cur.toggle, ...partial.toggle },
                    dose: { ...DEFAULT_AUTOMATION_SAFETY.dose, ...cur.dose, ...partial.dose },
                    hysteresis: {
                        ...DEFAULT_AUTOMATION_SAFETY.hysteresis,
                        ...cur.hysteresis,
                        ...partial.hysteresis,
                    },
                },
            }
        })
    }

    const inputCls =
        'mt-1 w-full max-w-[10rem] rounded-md border border-zinc-600 bg-zinc-950 px-3 py-2 text-zinc-50'

    const hintCls = 'mt-2 list-inside list-disc space-y-1 text-xs text-zinc-500'

    return (
        <div className="space-y-6">
            <div className="rounded-md border border-amber-900/50 bg-amber-950/20 px-3 py-3 text-sm text-amber-100/90">
                <p className="font-medium text-amber-100">What this section does</p>
                <p className="mt-1 text-xs leading-relaxed text-amber-100/80">
                    These values are saved on this system in Mongo and read by the{' '}
                    <strong className="font-medium text-amber-50">DeviceController</strong> Python app (the
                    machine on your LAN that talks to dosers and smart plugs). They do{' '}
                    <strong className="font-medium text-amber-50">not</strong> run in the cloud app by
                    themselves—they limit how often automation can send real hardware commands, and they add
                    climate deadband so outputs do not chatter. Save the system after changing numbers.
                </p>
            </div>

            {showToggleSection ? (
                <section className="rounded-lg border border-zinc-700 bg-zinc-950/40 p-4">
                <h3 className="mb-1 text-sm font-medium text-zinc-200">Smart plugs &amp; outlets (toggle)</h3>
                <p className="text-xs text-zinc-500">
                    Applies to <strong className="text-zinc-400">VeSync / smart plug</strong> commands:
                    lights, fans, heaters, humidifiers, etc. One command to one outlet counts as{' '}
                    <strong className="text-zinc-400">one toggle</strong>.
                </p>
                <ul className={hintCls}>
                    <li>
                        <strong className="text-zinc-400">Min interval</strong> — For each specific outlet, wait
                        at least this many seconds before sending another on/off. Stops rapid repeat commands to
                        the same relay.
                    </li>
                    <li>
                        <strong className="text-zinc-400">Max per hour / day</strong> — Cap on{' '}
                        <em>all</em> toggle commands added together (every outlet). Prevents runaway loops if a
                        sensor misbehaves.
                    </li>
                </ul>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Min interval (seconds)
                        </label>
                        <input
                            type="number"
                            min={0}
                            max={3600}
                            value={toggle.min_interval_seconds}
                            onChange={(e) =>
                                patch({
                                    toggle: {
                                        ...toggle,
                                        min_interval_seconds: clampInt(
                                            Number(e.target.value),
                                            0,
                                            3600,
                                            DEFAULT_AUTOMATION_SAFETY.toggle.min_interval_seconds
                                        ),
                                    },
                                })
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Max toggles / hour (all outlets)
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={2000}
                            value={toggle.max_actions_per_hour}
                            onChange={(e) =>
                                patch({
                                    toggle: {
                                        ...toggle,
                                        max_actions_per_hour: clampInt(
                                            Number(e.target.value),
                                            1,
                                            2000,
                                            DEFAULT_AUTOMATION_SAFETY.toggle.max_actions_per_hour
                                        ),
                                    },
                                })
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Max toggles / day (all outlets)
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={20000}
                            value={toggle.max_actions_per_day}
                            onChange={(e) =>
                                patch({
                                    toggle: {
                                        ...toggle,
                                        max_actions_per_day: clampInt(
                                            Number(e.target.value),
                                            1,
                                            20000,
                                            DEFAULT_AUTOMATION_SAFETY.toggle.max_actions_per_day
                                        ),
                                    },
                                })
                            }
                            className={inputCls}
                        />
                    </div>
                </div>
                </section>
            ) : null}

            {showDoseSection ? (
                <section className="rounded-lg border border-zinc-700 bg-zinc-950/40 p-4">
                <h3 className="mb-1 text-sm font-medium text-zinc-200">Doser &amp; mixer (dose)</h3>
                <p className="text-xs text-zinc-500">
                    Applies to <strong className="text-zinc-400">HTTP runs</strong> on the pump controller:
                    nutrient lines A/B/C, pH up/down, and the mixer/stir relay. Each successful run counts as{' '}
                    <strong className="text-zinc-400">one dose</strong> (mixer-only runs count too).
                </p>
                <ul className={hintCls}>
                    <li>
                        Limits are <strong className="text-zinc-400">separate</strong> from smart-plug toggles,
                        so you can allow fewer pump runs per hour than outlet flickers.
                    </li>
                    <li>
                        Separate from the regular Dosing section timing: those values tune normal recipe behavior,
                        while these limits still cap every real pump/mixer command the controller sends.
                    </li>
                    <li>
                        <strong className="text-zinc-400">Min interval</strong> — Per pump channel and role (for
                        example, the same nutrient head cannot dose again until this many seconds have passed).
                    </li>
                </ul>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Min interval (seconds)
                        </label>
                        <input
                            type="number"
                            min={0}
                            max={3600}
                            value={dose.min_interval_seconds}
                            onChange={(e) =>
                                patch({
                                    dose: {
                                        ...dose,
                                        min_interval_seconds: clampInt(
                                            Number(e.target.value),
                                            0,
                                            3600,
                                            DEFAULT_AUTOMATION_SAFETY.dose.min_interval_seconds
                                        ),
                                    },
                                })
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Max doses / hour (all pumps)
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={2000}
                            value={dose.max_actions_per_hour}
                            onChange={(e) =>
                                patch({
                                    dose: {
                                        ...dose,
                                        max_actions_per_hour: clampInt(
                                            Number(e.target.value),
                                            1,
                                            2000,
                                            DEFAULT_AUTOMATION_SAFETY.dose.max_actions_per_hour
                                        ),
                                    },
                                })
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Max doses / day (all pumps)
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={20000}
                            value={dose.max_actions_per_day}
                            onChange={(e) =>
                                patch({
                                    dose: {
                                        ...dose,
                                        max_actions_per_day: clampInt(
                                            Number(e.target.value),
                                            1,
                                            20000,
                                            DEFAULT_AUTOMATION_SAFETY.dose.max_actions_per_day
                                        ),
                                    },
                                })
                            }
                            className={inputCls}
                        />
                    </div>
                </div>
                </section>
            ) : null}

            {showHysteresisSection ? (
                <section className="rounded-lg border border-zinc-700 bg-zinc-950/40 p-4">
                <h3 className="mb-1 text-sm font-medium text-zinc-200">Climate deadband (hysteresis)</h3>
                <p className="text-xs text-zinc-500">
                    After heating/cooling or humidify/dehumidify turns on, the reading must move back into
                    range by <em>this extra margin</em> before outputs turn off. Reduces rapid cycling when the
                    sensor sits near the threshold.
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Temperature (°)
                        </label>
                        <input
                            type="number"
                            step="0.05"
                            min={0}
                            max={10}
                            value={hysteresis.temperature}
                            onChange={(e) =>
                                patch({
                                    hysteresis: {
                                        ...hysteresis,
                                        temperature: clampFloat(
                                            Number(e.target.value),
                                            0,
                                            10,
                                            DEFAULT_AUTOMATION_SAFETY.hysteresis.temperature
                                        ),
                                    },
                                })
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Humidity (%)
                        </label>
                        <input
                            type="number"
                            step="0.1"
                            min={0}
                            max={20}
                            value={hysteresis.humidity}
                            onChange={(e) =>
                                patch({
                                    hysteresis: {
                                        ...hysteresis,
                                        humidity: clampFloat(
                                            Number(e.target.value),
                                            0,
                                            20,
                                            DEFAULT_AUTOMATION_SAFETY.hysteresis.humidity
                                        ),
                                    },
                                })
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Default (other loops)
                        </label>
                        <input
                            type="number"
                            step="0.05"
                            min={0}
                            max={10}
                            value={hysteresis.default}
                            onChange={(e) =>
                                patch({
                                    hysteresis: {
                                        ...hysteresis,
                                        default: clampFloat(
                                            Number(e.target.value),
                                            0,
                                            10,
                                            DEFAULT_AUTOMATION_SAFETY.hysteresis.default
                                        ),
                                    },
                                })
                            }
                            className={inputCls}
                        />
                    </div>
                </div>
                </section>
            ) : null}

            {!showToggleSection && !showDoseSection && !showHysteresisSection ? (
                <div className="rounded-md border border-zinc-700 bg-zinc-950/30 px-3 py-2 text-xs text-zinc-400">
                    No eligible hardware/control loops are configured for automation safety limits yet.
                </div>
            ) : null}
        </div>
    )
}

export default AutomationSafetySettings
